import { minimizeBody, needsFeatureSession } from "../features/privacy/requestPolicy";
import { featureRequest } from "../features/privacy/featureSession";
import { privateFetch, readPrivacyResponse } from "../features/privacy/privateTransport";
import { getPrivacyPreferences, isConsentGranted, privacyPurposeForRequest, subscribePrivacyPreferences } from "../features/privacy/privacyPreferences";

const trimTrailingSlash = (value = "") => value.replace(/\/+$/, "");
const LOCAL_API_BASE = "http://127.0.0.1:3001";
const LIVE_API_BASE = "https://my-backend-qxay.onrender.com";

function isLocalRuntime() {
  if (process.env.NODE_ENV === "development") return true;
  if (typeof window === "undefined") return false;
  return ["localhost", "127.0.0.1", "::1", "[::1]"].includes(
    window.location.hostname
  );
}

function dedupe(values) {
  return [...new Set(values.filter(Boolean))];
}

export function getApiBase() {
  const base = (process.env.REACT_APP_API_BASE || "").trim();
  if (base) return trimTrailingSlash(base);

  // Local development runs the Express control plane on port 3001. Keep the
  // hosted API as the production default, but do not send an unauthenticated
  // local dashboard request to the protected production deployment.
  if (isLocalRuntime()) {
    return trimTrailingSlash(
      (process.env.REACT_APP_LOCAL_API_BASE || LOCAL_API_BASE).trim()
    );
  }

  return LIVE_API_BASE;
}

export function getApiBaseCandidates() {
  const explicitBase = trimTrailingSlash(
    (process.env.REACT_APP_API_BASE || "").trim()
  );
  const localBase =
    isLocalRuntime()
      ? trimTrailingSlash(
          (process.env.REACT_APP_LOCAL_API_BASE || LOCAL_API_BASE).trim()
        )
      : "";

  return dedupe([explicitBase, localBase, isLocalRuntime() ? "" : LIVE_API_BASE]);
}

export function getPossibleWorldsApiBase() {
  const dedicatedBase = trimTrailingSlash(
    (process.env.REACT_APP_POSSIBLE_WORLDS_API_BASE || "").trim()
  );
  if (dedicatedBase) return dedicatedBase;
  return process.env.NODE_ENV === "development" ? "http://127.0.0.1:3002" : "";
}

export function apiUrl(path) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const base = getApiBase();
  return base ? `${base}${normalizedPath}` : normalizedPath;
}

export function possibleWorldsApiUrl(path) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const base = getPossibleWorldsApiBase();
  return base ? `${base}${normalizedPath}` : normalizedPath;
}

export function getThinkUrl() {
  const explicitThinkUrl = (process.env.REACT_APP_API_URL || "").trim();
  return explicitThinkUrl || apiUrl("/api/think");
}

function getFallbackCandidates(input, { allowHostedFallback = false } = {}) {
  if (typeof input !== "string") {
    return [input];
  }

  if (input.startsWith("/api/")) {
    const bases = dedupe([
      ...getApiBaseCandidates(),
      ...(allowHostedFallback ? [LIVE_API_BASE] : []),
    ]);
    return bases.length ? bases.map((base) => `${base}${input}`) : [input];
  }

  try {
    const parsed = new URL(
      input,
      typeof window !== "undefined" ? window.location.origin : "http://localhost"
    );
    const normalizedPath = `${parsed.pathname}${parsed.search}`;

    if (!normalizedPath.startsWith("/api/")) {
      return [input];
    }

    const bases = dedupe([
      ...getApiBaseCandidates(),
      ...(allowHostedFallback ? [LIVE_API_BASE] : []),
    ]);
    return dedupe([
      input,
      ...bases.map((base) => `${base}${normalizedPath}`),
    ]);
  } catch {
    return [input];
  }
}

function isRetryableFetchError(error) {
  return error instanceof TypeError || error?.name === "TypeError";
}

export async function apiFetch(input, init) {
  const purpose = privacyPurposeForRequest(input, init?.method);
  if (!purpose) {
    const url = new URL(typeof input === "string" ? input : input.url, window.location.origin);
    if (needsFeatureSession(url.pathname, init?.method || "GET")) {
      const base = getApiBase();
      const authority = new URL(base || window.location.origin, window.location.origin).origin;
      if (url.origin !== authority && !String(input).startsWith("/api/")) throw new Error("Feature request authority mismatch");
      return featureRequest(base, url.pathname + url.search, minimizeFeatureInit(url.pathname, init));
    }
    return performApiFetch(input, init);
  }
  if (!isConsentGranted(purpose)) {
    const error = new Error("This SI service is not enabled in Privacy settings");
    error.name = "PrivacyPreferenceError";
    throw error;
  }
  const controller = new AbortController();
  const abort = () => controller.abort();
  const pauseWhenHidden = () => { if (document.hidden) abort(); };
  document.addEventListener("visibilitychange", pauseWhenHidden);
  if (init?.signal?.aborted) abort();
  init?.signal?.addEventListener("abort", abort);
  const unsubscribe = subscribePrivacyPreferences(() => {
    if (!isConsentGranted(purpose)) abort();
  });
  try {
    const url = new URL(typeof input === "string" ? input : input.url, window.location.origin);
    const authority = new URL(getApiBase() || window.location.origin, window.location.origin).origin;
    if(url.origin !== authority && !String(input).startsWith("/api/")) throw new Error("Feature request authority mismatch");
    if(document.hidden) throw new Error("Cloud assistance pauses while this tab is hidden");
    return await featureRequest(getApiBase(), url.pathname, minimizeFeatureInit(url.pathname, { ...init, signal: controller.signal }));
  } finally {
    unsubscribe();
    document.removeEventListener("visibilitychange", pauseWhenHidden);
    init?.signal?.removeEventListener("abort", abort);
  }
}

async function performApiFetch(input, init) {
  const candidates = getFallbackCandidates(input, {
    allowHostedFallback: init?.allowHostedFallback === true,
  });
  let lastError = null;
  const retryHttpStatuses = Array.isArray(init?.retryHttpStatuses)
    ? init.retryHttpStatuses
    : [];
  const fetchInit = init && typeof init === "object"
    ? Object.fromEntries(
        Object.entries(init).filter(
          ([key]) => key !== "retryHttpStatuses" && key !== "allowHostedFallback"
        )
      )
    : init;

  for (let index = 0; index < candidates.length; index += 1) {
    const candidate = candidates[index];

    const purpose = privacyPurposeForRequest(candidate, fetchInit?.method);
    if (purpose && !isConsentGranted(purpose)) {
      throw new Error("This SI service is not enabled in Privacy settings");
    }

    try {
      if (index > 0) {
        console.warn(`⚠️ API fallback -> ${candidate}`);
      }
      const response = await fetch(candidate, fetchInit);
      if (
        !response.ok &&
        retryHttpStatuses.includes(response.status) &&
        index < candidates.length - 1
      ) {
        lastError = new Error(`API ${response.status} from ${candidate}`);
        continue;
      }
      return response;
    } catch (error) {
      lastError = error;
      if (!isRetryableFetchError(error) || index === candidates.length - 1) {
        throw error;
      }
    }
  }

  throw lastError || new Error("API request failed");
}

export async function fetchJson(input, init) {
  const purpose=privacyPurposeForRequest(input,init?.method);
  const receipt=purpose?getPrivacyPreferences().receipts.find(item=>item.purpose===purpose)?.id:null;
  const response = await apiFetch(input, init);
  const text = await response.text();
  if(purpose&&(!isConsentGranted(purpose)||getPrivacyPreferences().receipts.find(item=>item.purpose===purpose)?.id!==receipt))throw new Error('Service permission changed while receiving the response');

  if (!response.ok) {
    throw new Error(`API ${response.status}: ${text.slice(0, 200)}`);
  }

  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch (error) {
    throw new Error(`Expected JSON response, received: ${text.slice(0, 200)}`);
  }
}

export async function privacyRequest(path, init) {
  const purpose=privacyPurposeForRequest(path,init?.method);
  const receipt=purpose?getPrivacyPreferences().receipts.find(item=>item.purpose===purpose)?.id:null;
  if(purpose&&!isConsentGranted(purpose)) throw new Error("This SI service is not enabled in Privacy settings");
  const response = purpose ? await apiFetch(path, init) : await featureRequest(getApiBase(), path, init);
  const data = await readPrivacyResponse(response);
  if(purpose&&(!isConsentGranted(purpose)||getPrivacyPreferences().receipts.find(item=>item.purpose===purpose)?.id!==receipt))throw new Error('Service permission changed while receiving the response');
  if(!response.ok) throw new Error(data.error || "Privacy request failed");
  return data;
}
function minimizeFeatureInit(path, init) {
  if(!init?.body || typeof init.body !== "string") return init;
  try { return {...init, body: JSON.stringify(minimizeBody(JSON.parse(init.body), path))}; }
  catch { return init; }
}

export async function recoveryRequest(action, secret) {
  if(!["export","delete","delete-key"].includes(action)) throw new Error("Unsupported recovery action");
  const response=await privateFetch(`${getApiBase()}/api/privacy/${action}`,{method:"POST",headers:{"Content-Type":"application/json","X-Privacy-Session":secret},body:JSON.stringify({useRecovery:true})});
  const data=await readPrivacyResponse(response);
  if(!response.ok) throw new Error(data.error||"Recovery request failed");
  if(action==='export' ? (!data.session||!data.inventory||!Array.isArray(data.resources)||!Array.isArray(data.receipts)||!Array.isArray(data.jobs)) : (typeof data.jobId!=='string'||data.status!=='external_review_pending'||typeof data.keyRevoked!=='boolean'||(action==='delete-key'&&!data.keyRevoked)))throw new Error('Data controls returned an incomplete result. Please retry.');
  return data;
}
